import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
const PROFILE_KEY = (userId) => ['profile', userId ?? 'anon'];
/**
 * Profil du joueur courant. Renvoie `null` si non authentifié ou pas encore
 * de ligne profile (le trigger handle_new_user devrait l'avoir créée).
 */
export function useCurrentProfile() {
    const { user, status } = useAuth();
    return useQuery({
        queryKey: PROFILE_KEY(user?.id),
        enabled: status === 'ready' && !!user?.id,
        queryFn: async () => {
            if (!user?.id)
                return null;
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/**
 * Profil public par pseudo (CITEXT côté DB → comparaison case-insensitive).
 */
export function useProfileByPseudo(pseudo) {
    return useQuery({
        queryKey: ['profile', 'pseudo', pseudo?.toLowerCase()],
        enabled: !!pseudo && pseudo.length >= 2,
        queryFn: async () => {
            if (!pseudo)
                return null;
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('pseudo', pseudo)
                .eq('is_public', true)
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
    });
}
/**
 * Mutation : change_pseudo. Mappe les codes Postgres aux codes métier connus.
 */
export function useChangePseudo() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (newPseudo) => {
            const { error } = await supabase.rpc('change_pseudo', { p_new_pseudo: newPseudo });
            if (error) {
                const code = (error.message || '').toLowerCase();
                if (code.includes('pseudo_length'))
                    throw asTaggedError('pseudo_length');
                if (code.includes('pseudo_format'))
                    throw asTaggedError('pseudo_format');
                if (code.includes('pseudo_taken'))
                    throw asTaggedError('pseudo_taken');
                if (code.includes('auth_required'))
                    throw asTaggedError('auth_required');
                if (code.includes('profile_not_found'))
                    throw asTaggedError('profile_not_found');
                throw asTaggedError('unknown', error.message);
            }
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: PROFILE_KEY(user?.id) });
        },
    });
}
function asTaggedError(code, message) {
    const err = new Error(message ?? code);
    err.code = code;
    return err;
}
/**
 * Détecte si le pseudo actuel est encore le placeholder créé au signup
 * (via le trigger handle_new_user). Utilisé pour proposer l'onboarding.
 */
export function isPlaceholderPseudo(pseudo) {
    if (!pseudo)
        return false;
    return /^Olympien[0-9a-f]{6}$/i.test(pseudo);
}
